import React, { useEffect, useState } from 'react';
import {
    Container,
    Typography,
    Grid,
    Box,
    CircularProgress,
    IconButton,
    Tooltip,
    Grow
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    getWantedBoard,
    addToWantedBoard,
    removeFromWantedBoard,
    getTwoLevelCategories
} from '../services/api';
import CategorySelector from '../components/CategorySelector';
import { StyledButton } from '../components/StyledComponents';

const StyledContainer = styled(Container)(({ theme }) => ({
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
}));

const CategoryCard = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    transition: 'background-color 0.3s ease',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const CategoryInfo = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
}));

const CategoryIcon = styled('img')({
    width: 40,
    height: 40,
    objectFit: 'contain',
    marginRight: 16,
});

function WantedBoard() {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectorOpen, setSelectorOpen] = useState(false);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        fetchWantedEntries();
        fetchCategories();
    }, []);

    const fetchWantedEntries = async () => {
        try {
            setLoading(true);
            const response = await getWantedBoard(true);
            setEntries(response.data);
        } catch (error) {
            console.error('Error fetching wanted board:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await getTwoLevelCategories();
            setCategories(response.data);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const handleAddClick = () => {
        setSelectorOpen(true);
    };

    const handleCategorySelect = async (category) => {
        try {
            await addToWantedBoard(category.id);
            setSelectorOpen(false);
            fetchWantedEntries();
        } catch (error) {
            console.error('Error adding category:', error);
        }
    };

    const handleRemove = async (categoryId) => {
        try {
            await removeFromWantedBoard(categoryId);
            setEntries(entries.filter((e) => e.category_id !== categoryId));
        } catch (error) {
            console.error('Error removing category:', error);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <StyledContainer maxWidth="md">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                    Wanted Board
                </Typography>
                <StyledButton variant="contained" color="primary" onClick={handleAddClick} startIcon={<AddIcon />}>Add Category</StyledButton>
            </Box>
            <Typography variant="body1" color="textSecondary" mb={2}>
                Add categories to your wanted board and we'll notify you whenever a new listing is posted in those categories or any of their subcategories.
            </Typography>
            <Typography variant="body2" color="textSecondary" mb={4}>
                You'll receive an email or push notification as soon as a matching listing goes live.
            </Typography>
            <Grid container spacing={2}>
                {entries.map((entry) => (
                    <Grow in key={entry.id} timeout={400}>
                        <Grid item xs={12} sm={6} md={4}>
                            <CategoryCard>
                                <CategoryInfo>
                                    <CategoryIcon
                                        src={`${process.env.PUBLIC_URL}/icons/${entry.category_id}.png`}
                                        alt={entry.category?.name}
                                        onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = `${process.env.PUBLIC_URL}/icons/X.png`;
                                    }}
                                />
                                <Typography>{entry.category?.name || entry.category_id}</Typography>
                            </CategoryInfo>
                            <Tooltip title="Remove">
                                <IconButton onClick={() => handleRemove(entry.category_id)}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                            </CategoryCard>
                        </Grid>
                    </Grow>
                ))}
                {entries.length === 0 && (
                    <Grid item xs={12}>
                        <Typography variant="body1" align="center" color="textSecondary">
                            No wanted categories yet
                        </Typography>
                    </Grid>
                )}
            </Grid>
            <CategorySelector
                open={selectorOpen}
                handleClose={() => setSelectorOpen(false)}
                categories={categories}
                handleCategorySelect={handleCategorySelect}
                handleCategoryBack={() => {}}
                hasBackButton={false}
            />
        </StyledContainer>
    );
}

export default WantedBoard;
